import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Calculator, TrendingUp, Leaf, DollarSign, Activity } from "lucide-react";
import { Slider } from "@/components/ui/slider";

const DIESEL_LITRES_PER_KWH = 0.28;       // litres per kWh produced
const CO2_PER_LITRE = 2.68;               // kg CO2 per litre of diesel
const MBESS_COST_PER_KWH = 0.18;          // $/kWh delivered (EaaS)

export default function ROICalculator({ embedded = false }) {
  const [power, setPower] = useState(150);            // kW
  const [hours, setHours] = useState(12);             // hours/day
  const [fuelCost, setFuelCost] = useState(1.2);      // $/litre
  const [days, setDays] = useState(180);              // project duration in days

  const r = useMemo(() => {
    const kwh = power * hours * days;
    const litres = kwh * DIESEL_LITRES_PER_KWH;
    const dieselCost = litres * fuelCost;
    const mbessCost = kwh * MBESS_COST_PER_KWH;
    const saved = Math.max(0, dieselCost - mbessCost);
    const co2 = (litres * CO2_PER_LITRE) / 1000;     // tonnes
    const roiPct = mbessCost > 0 ? (saved / mbessCost) * 100 : 0;
    return { kwh, dieselCost, mbessCost, saved, co2, roiPct };
  }, [power, hours, fuelCost, days]);

  const fmt$ = (n) => "$" + Math.round(n).toLocaleString();

  return (
    <section className={embedded ? "" : "py-28"} data-testid="roi-calculator">
      <div className={embedded ? "" : "max-w-[1400px] mx-auto px-6 lg:px-10"}>
        {!embedded && (
          <div className="max-w-3xl mb-12">
            <span className="eyebrow">ROI Calculator</span>
            <h2 className="heading-section mt-4">
              See the savings in <span className="text-tratou-green">real numbers.</span>
            </h2>
          </div>
        )}

        <div className="grid lg:grid-cols-5 gap-px bg-white/10 border border-white/10">
          <div className="lg:col-span-2 bg-tratou-deep p-8 space-y-7">
            <div className="flex items-center gap-2 mb-2">
              <Calculator className="w-5 h-5 text-tratou-green" />
              <p className="eyebrow">Inputs</p>
            </div>

            <SliderRow label="Power Required" value={power} unit="kW" min={10} max={1000} step={5} onChange={setPower} testid="roi-power" />
            <SliderRow label="Hours per Day" value={hours} unit="hrs" min={1} max={24} step={1} onChange={setHours} testid="roi-hours" />
            <SliderRow label="Fuel Cost" value={fuelCost.toFixed(2)} unit="$ / L" min={0.4} max={3.0} step={0.05} onChange={setFuelCost} testid="roi-fuel" />
            <SliderRow label="Project Duration" value={days} unit="days" min={7} max={730} step={1} onChange={setDays} testid="roi-days" />
          </div>

          <div className="lg:col-span-3 bg-tratou-void p-8 grid sm:grid-cols-2 gap-6">
            <ResultCard icon={Activity} label="Energy Required" value={`${Math.round(r.kwh).toLocaleString()} kWh`} testid="roi-r-kwh" />
            <ResultCard icon={DollarSign} label="Diesel Cost" value={fmt$(r.dieselCost)} testid="roi-r-diesel" />
            <ResultCard icon={Leaf} label="CO₂ Avoided" value={`${r.co2.toFixed(1)} t`} highlight testid="roi-r-co2" />
            <ResultCard icon={TrendingUp} label="Money Saved" value={fmt$(r.saved)} highlight big testid="roi-r-saved" />
            <div className="sm:col-span-2 panel p-6 flex items-center justify-between">
              <div>
                <p className="eyebrow-dim">ROI vs Diesel</p>
                <p className="font-display text-5xl font-bold text-tratou-green mt-2" data-testid="roi-r-pct">
                  {Math.round(r.roiPct)}%
                </p>
              </div>
              <div className="text-right">
                <p className="eyebrow-dim">EaaS Cost</p>
                <p className="font-mono text-xl text-white mt-2">{fmt$(r.mbessCost)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SliderRow({ label, value, unit, min, max, step, onChange, testid }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <span className="field-label !mb-0">{label}</span>
        <span className="font-mono text-tratou-green text-sm" data-testid={`${testid}-value`}>{value} {unit}</span>
      </div>
      <Slider
        min={min}
        max={max}
        step={step}
        value={[Number(value)]}
        onValueChange={(v) => onChange(v[0])}
        data-testid={testid}
      />
    </div>
  );
}

function ResultCard({ icon: Icon, label, value, highlight, big, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`panel p-5 ${highlight ? "border-tratou-green/40" : ""}`}
      data-testid={testid}
    >
      <Icon className={`w-5 h-5 mb-3 ${highlight ? "text-tratou-green" : "text-tratou-dim"}`} />
      <p className="eyebrow-dim">{label}</p>
      <p className={`mt-2 font-display font-bold ${big ? "text-4xl text-tratou-green" : "text-2xl text-white"}`}>{value}</p>
    </motion.div>
  );
}
